import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileSignature, FileText, FolderOpen, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function PortalInicio() {
  const { perfil } = useAuth();
  const [stats, setStats] = useState({ contratosPendientes: 0, contratosVigentes: 0, informes: 0, documentos: 0 });
  const [proximosVencimientos, setProximosVencimientos] = useState<any[]>([]);

  useEffect(() => {
    if (!perfil?.user_id) return;
    (async () => {
      const [contratos, informes, docs] = await Promise.all([
        (supabase as any).from('contratos_mantenimiento').select('id, numero_formateado, estado, fecha_fin').order('fecha_fin', { ascending: true }),
        (supabase as any).from('informes_consolidados').select('id'),
        (supabase as any).from('portal_documentos').select('id'),
      ]);
      const cs = (contratos.data ?? []) as any[];
      setStats({
        contratosPendientes: cs.filter(c => c.estado === 'pendiente_firma' || c.estado === 'borrador').length,
        contratosVigentes: cs.filter(c => c.estado === 'vigente').length,
        informes: informes.data?.length ?? 0,
        documentos: docs.data?.length ?? 0,
      });
      setProximosVencimientos(cs.filter(c => c.estado === 'vigente').slice(0, 3));
    })();
  }, [perfil?.user_id]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Bienvenido, {perfil?.nombre}</h1>
        <p className="text-muted-foreground">Resumen de tu cuenta</p>
      </div>

      {stats.contratosPendientes > 0 && (
        <Card className="border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Tienes {stats.contratosPendientes} contrato(s) pendiente(s) de firma</p>
              <Button asChild size="sm" className="mt-2">
                <Link to="/portal/contratos">Ver contratos</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={FileSignature} label="Contratos vigentes" value={stats.contratosVigentes} to="/portal/contratos" />
        <StatCard icon={FileSignature} label="Pendientes firma" value={stats.contratosPendientes} to="/portal/contratos" />
        <StatCard icon={FileText} label="Informes" value={stats.informes} to="/portal/informes" />
        <StatCard icon={FolderOpen} label="Documentos" value={stats.documentos} to="/portal/documentos" />
      </div>

      {proximosVencimientos.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Próximos vencimientos</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {proximosVencimientos.map(c => (
              <div key={c.id} className="flex justify-between items-center text-sm border-b pb-2 last:border-b-0">
                <span className="font-mono">{c.numero_formateado}</span>
                <span className="text-muted-foreground">
                  {c.fecha_fin ? format(new Date(c.fecha_fin), "d 'de' MMMM yyyy", { locale: es }) : '—'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, to }: any) {
  return (
    <Link to={to}>
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <CardContent className="pt-6 text-center space-y-1">
          <Icon className="h-6 w-6 mx-auto text-primary" />
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </CardContent>
      </Card>
    </Link>
  );
}
